/**
 * RatePlanComparison — Side-by-side rate plans table for hotel detail page.
 *
 * Props:
 *   hotelId        — string, required
 *   roomTypeId     — string, filter plans for this room
 *   adults, children, nights — booking context
 *   currency       — display currency code (default TRY)
 *   onSelect(plan) — called when user clicks "Rezerve Et"
 */
import React, { useEffect, useState } from "react";
import { Check, X, Star } from "lucide-react";
import api, { formatTRY } from "@/lib/api";

function CancellationBadge({ text }) {
    const t = (text || "").toLowerCase();
    let cls = "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
    if (t.includes("iade yok") || t.includes("iade edilemez") || t.includes("non-refundable")) {
        cls = "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300";
    } else if (t.includes("kısıtlı") || t.includes("kısmi")) {
        cls = "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
    }
    return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${cls}`}>{text || "—"}</span>;
}

function boardIcon(board) {
    if (!board) return null;
    const b = board.toLowerCase();
    if (b.includes("her şey")) return "🍽️ Her Şey Dahil";
    if (b.includes("yarım")) return "🥐 Yarım Pansiyon";
    if (b.includes("tam")) return "🍴 Tam Pansiyon";
    if (b.includes("kahvalt")) return "🍳 Kahvaltı Dahil";
    if (b.includes("sade") || b.includes("oda")) return "🛏️ Sadece Oda";
    return board;
}

export default function RatePlanComparison({ hotelId, roomTypeId, adults = 2, children = 0, nights = 1, checkIn = "", onSelect }) {
    const [plans, setPlans] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!hotelId) return;
        setLoading(true);
        const params = new URLSearchParams({
            adults: String(adults),
            children: String(children),
            nights: String(Math.max(1, nights)),
        });
        if (roomTypeId) params.set("room_type_id", roomTypeId);
        if (checkIn) params.set("check_in", checkIn);
        api.get(`/hotels/${hotelId}/comparison-plans?${params.toString()}`)
            .then((r) => setPlans(r.data?.plans || []))
            .catch(() => setPlans([]))
            .finally(() => setLoading(false));
    }, [hotelId, roomTypeId, adults, children, nights, checkIn]);

    if (loading) return <div className="h-32 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />;
    if (!plans || plans.length === 0) {
        return <div className="text-xs italic text-slate-400 p-4 text-center">Bu oda için uygun fiyat planı bulunamadı.</div>;
    }

    return (
        <div className="border-t border-slate-200 dark:border-slate-700" data-testid="rate-plan-comparison">
            <div className="px-5 py-3 bg-slate-50 dark:bg-slate-800/40 flex items-center justify-between">
                <h4 className="font-display font-bold text-slate-900 dark:text-white text-sm">Fiyat Seçenekleri</h4>
                <span className="text-[11px] text-slate-500">{plans.length} plan · {Math.max(1, nights)} gece için</span>
            </div>

            {/* DESKTOP TABLE */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-slate-50/40 dark:bg-slate-800/30 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                        <tr>
                            <th className="px-5 py-3 text-left">Plan</th>
                            <th className="px-3 py-3 text-center">Yemek</th>
                            <th className="px-3 py-3 text-center">İptal</th>
                            <th className="px-5 py-3 text-right">{Math.max(1, nights)} Gece Fiyat</th>
                            <th className="px-5 py-3 text-right">İşlem</th>
                        </tr>
                    </thead>
                    <tbody>
                        {plans.map((p) => (
                            <tr key={p.id} data-testid={`plan-row-${p.id}`}
                                className={`border-t border-slate-100 dark:border-slate-800/50 ${p.unavailable_reason ? "opacity-50" : ""} ${p.is_recommended && !p.unavailable_reason ? "bg-sky-50/30 dark:bg-sky-950/10" : ""}`}>
                                <td className="px-5 py-4 relative">
                                    {p.is_recommended && !p.unavailable_reason && (
                                        <div className="absolute -top-2 left-5 px-2 py-0.5 rounded-full bg-sky-500 text-white text-[9px] font-bold uppercase tracking-wider flex items-center gap-1">
                                            <Star className="h-2.5 w-2.5 fill-white" /> EN ÇOK SEÇİLEN
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <span className="font-semibold text-slate-900 dark:text-white">{p.name}</span>
                                        {p.badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-950/60 dark:text-purple-300 uppercase">{p.badge}</span>}
                                    </div>
                                    {p.description && <div className="text-xs text-slate-500 mt-0.5">{p.description}</div>}
                                    {p.unavailable_reason && <div className="text-[11px] text-red-600 dark:text-red-400 mt-1 font-medium">⚠ {p.unavailable_reason}</div>}
                                </td>
                                <td className="px-3 py-4 text-center text-xs text-slate-700 dark:text-slate-200">{boardIcon(p.board)}</td>
                                <td className="px-3 py-4 text-center"><CancellationBadge text={p.cancellation} /></td>
                                <td className="px-5 py-4 text-right">
                                    {p.original_price && p.discount_pct > 0 && (
                                        <div className="text-xs text-slate-400 line-through">{formatTRY(p.original_price)}</div>
                                    )}
                                    <div className={`font-display text-xl font-bold ${p.discount_pct > 0 ? "text-emerald-600" : "text-slate-900 dark:text-white"}`}>{formatTRY(p.total)}</div>
                                    {p.discount_pct > 0 && <div className="text-[10px] text-emerald-600 font-bold">%{p.discount_pct} indirim</div>}
                                </td>
                                <td className="px-5 py-4 text-right">
                                    <button
                                        data-testid={`select-plan-${p.id}`}
                                        onClick={() => !p.unavailable_reason && onSelect && onSelect(p)}
                                        disabled={!!p.unavailable_reason}
                                        className={`px-4 py-2 rounded-xl font-bold text-xs whitespace-nowrap transition-colors ${p.unavailable_reason ? "bg-slate-100 dark:bg-slate-800 text-slate-400 cursor-not-allowed" : p.is_recommended ? "bg-sky-500 hover:bg-sky-600 text-white shadow-md shadow-sky-500/30" : "bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 hover:border-sky-500 text-slate-700 dark:text-slate-200"}`}
                                    >
                                        {p.unavailable_reason ? "Uygun Değil" : (p.is_recommended ? "Rezerve Et →" : "Rezerve Et")}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* MOBILE CARD STACK */}
            <div className="md:hidden p-3 space-y-3">
                {plans.map((p) => (
                    <div key={p.id} data-testid={`plan-card-${p.id}`}
                        className={`rounded-2xl p-4 border-2 ${p.is_recommended ? "border-sky-500 bg-sky-50/30 dark:bg-sky-950/10 shadow-md" : "border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900"} relative`}>
                        {p.is_recommended && (
                            <div className="absolute -top-2 left-4 px-2 py-0.5 rounded-full bg-sky-500 text-white text-[9px] font-bold uppercase tracking-wider">EN ÇOK SEÇİLEN</div>
                        )}
                        <div className="flex justify-between items-start gap-2 mb-2 mt-1">
                            <div className="flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <div className="font-bold text-slate-900 dark:text-white">{p.name}</div>
                                    {p.badge && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 uppercase">{p.badge}</span>}
                                </div>
                                {p.description && <div className="text-[10px] text-slate-500 mt-0.5">{p.description}</div>}
                            </div>
                            <div className="text-right flex-shrink-0">
                                {p.original_price && p.discount_pct > 0 && <div className="text-[10px] text-slate-400 line-through">{formatTRY(p.original_price)}</div>}
                                <div className={`font-display text-lg font-bold ${p.discount_pct > 0 ? "text-emerald-600" : "text-slate-900 dark:text-white"}`}>{formatTRY(p.total)}</div>
                                {p.discount_pct > 0 && <div className="text-[9px] text-emerald-600 font-bold">%{p.discount_pct} indirim</div>}
                            </div>
                        </div>
                        <div className="flex gap-1 flex-wrap mb-3">
                            <span className="px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 text-[10px] font-semibold">{boardIcon(p.board)}</span>
                            <CancellationBadge text={p.cancellation} />
                        </div>
                        <button
                            data-testid={`select-plan-mobile-${p.id}`}
                            onClick={() => onSelect && onSelect(p)}
                            className={`w-full px-4 py-2 rounded-xl font-bold text-sm transition-colors ${p.is_recommended ? "bg-sky-500 hover:bg-sky-600 text-white" : "bg-white dark:bg-slate-800 border-2 border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"}`}
                        >
                            Rezerve Et{p.is_recommended ? " →" : ""}
                        </button>
                    </div>
                ))}
            </div>
        </div>
    );
}
